// App instalável (PWA): registra o service worker e guarda o pedido de instalação do navegador
// (Chrome e Edge, no Android e no computador) para o menu "Instalar app" usar depois.
// No iPhone não há pedido: a instalação é Compartilhar → Adicionar à Tela de Início.
import { useSyncExternalStore } from 'react'

type PedidoInstalacao = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }

let pedido: PedidoInstalacao | null = null
const ouvintes = new Set<() => void>()
const avisar = () => ouvintes.forEach((f) => f())

/** Chamado uma vez, antes do React: o pedido pode chegar antes de qualquer tela montar. */
export function iniciarApp() {
  window.addEventListener('beforeinstallprompt', (e) => {
    pedido = e as PedidoInstalacao
    avisar()
  })
  window.addEventListener('appinstalled', () => {
    pedido = null
    avisar()
  })
  // Só no build publicado: no `npm run dev` o service worker atrapalharia o recarregamento do Vite.
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => undefined)
    })
  }
}

/** Aberto como app (pela tela inicial), não numa aba do navegador. */
export const abertoComoApp = () =>
  window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true

/** iPhone ou iPad (o iPad se apresenta como Mac, mas tem toque). */
export const ehIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)

export function useInstalarApp() {
  const temPedido = useSyncExternalStore(
    (f) => {
      ouvintes.add(f)
      return () => ouvintes.delete(f)
    },
    () => pedido !== null,
  )
  const instalado = abertoComoApp()
  return {
    /** O navegador oferece a instalação direto (Android, computador). */
    temPedido: temPedido && !instalado,
    /** No iPhone, só com instruções. */
    precisaInstrucoes: !temPedido && !instalado && ehIOS(),
    async instalar(): Promise<boolean> {
      if (!pedido) return false
      await pedido.prompt()
      const { outcome } = await pedido.userChoice
      pedido = null
      avisar()
      return outcome === 'accepted'
    },
  }
}
